import { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

export default function PhotoCarousel({ photos, className = "" }) {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  const scrollPrev = useCallback(() => api?.scrollPrev(), [api]);
  const scrollNext = useCallback(() => api?.scrollNext(), [api]);

  return (
    <div className={`relative group ${className}`} data-testid="photo-carousel">
      <Carousel setApi={setApi} opts={{ loop: true }}>
        <CarouselContent>
          {photos.map((photo, i) => (
            <CarouselItem key={i}>
              <div className="photo-frame rounded-2xl overflow-hidden shadow-sm">
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full object-cover"
                  style={{
                    aspectRatio: photo.aspect || "4/3",
                    objectPosition: photo.position || "center",
                  }}
                  draggable="false"
                />
              </div>
              {photo.caption && (
                <p
                  className="text-xs sm:text-sm mt-2 sm:mt-2.5 font-light text-center"
                  style={{ color: "#C4B298", fontFamily: "'Manrope', sans-serif" }}
                >
                  {photo.caption}
                </p>
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Navigation arrows */}
      {count > 1 && (
        <>
          <button
            onClick={scrollPrev}
            className="absolute left-2.5 top-[45%] -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center opacity-80 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 shadow-sm"
            style={{ background: "rgba(253,251,247,0.92)", border: "1px solid #E6E2DD" }}
            data-testid="carousel-prev"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-4 h-4" style={{ color: "#2D2D2A" }} />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-2.5 top-[45%] -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center opacity-80 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 shadow-sm"
            style={{ background: "rgba(253,251,247,0.92)", border: "1px solid #E6E2DD" }}
            data-testid="carousel-next"
            aria-label="Next photo"
          >
            <ChevronRight className="w-4 h-4" style={{ color: "#2D2D2A" }} />
          </button>
        </>
      )}

      {/* Dots */}
      {count > 1 && (
        <div className="flex justify-center items-center gap-1.5 mt-3 sm:mt-3.5">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              onClick={() => api?.scrollTo(i)}
              className="p-1 focus:outline-none"
              aria-label={`Go to photo ${i + 1}`}
            >
              <span
                className="block rounded-full transition-all duration-300"
                style={{
                  width: i === current ? "16px" : "6px",
                  height: "6px",
                  background: i === current ? "#8A9A5B" : "#D4D0CB",
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
